import { Component } from '@theme/component';
import { isClickedOutside, isMobileBreakpoint, isTouchDevice, mediaQueryLarge } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * The reveal behaviour is driven by the `data-reveal-mode` attribute on the
 * closest `.product-hotspots` wrapper:
 * - `hover`  (default): opens on hover on desktop, toggles on tap on touch.
 * - `click`: the marker toggles the card open/closed on any device.
 * - `always`: the card is shown on load and stays open; the marker toggles it.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} trigger - The button element.
 * @property {HTMLAnchorElement} [productLink] - The product link element.
 *
 * @extends Component<Refs>
 */

export class ProductHotspotComponent extends Component {
  requiredRefs = ['trigger', 'dialog'];
  /** @type {(() => void) | null} */
  #pointerenterHandler = null;
  /** @type {number | null} */
  #timer = null;
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {number | null} */
  #rafId = null;

  /**
   * The reveal mode read from the closest hotspots wrapper.
   * @returns {'hover' | 'click' | 'always'}
   */
  get #revealMode() {
    const mode = this.closest('[data-reveal-mode]')?.getAttribute('data-reveal-mode');
    return mode === 'click' || mode === 'always' ? mode : 'hover';
  }

  /**
   * Wire up the reveal behaviour for the component's mode: `hover` binds
   * breakpoint-aware listeners, `always` opens the card and observes container
   * resizes to keep it positioned, and `click` needs no setup (the template's
   * `on:click` delegates to `handleHotspotClick`).
   * @returns {void}
   */
  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const mode = this.#revealMode;

    if (mode === 'hover') {
      // Set up initial event listeners based on current breakpoint
      this.#handleBreakpointChange();
      // Listen for breakpoint changes
      mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange, { signal: this.#controller.signal });
    } else if (mode === 'always') {
      // Show the card on load and keep it positioned as the container resizes
      this.#openAlways();

      const container = this.parentElement;
      if (container) {
        this.#resizeObserver = new ResizeObserver(() => this.#repositionIfOpen());
        this.#resizeObserver.observe(container);
      }
    }
    // 'click' mode needs no setup: the template's on:click delegates to handleHotspotClick.
  }

  /**
   * Tear down everything set up over the component's lifetime: desktop/breakpoint
   * listeners, any document- and dialog-level light-dismiss listeners still bound
   * from an open card, the AbortController, the ResizeObserver, and a pending rAF.
   * Runs even if disconnected while open (e.g. a slideshow re-rendering its slides)
   * so nothing leaks against a detached dialog.
   * @returns {void}
   */
  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up listeners
    this.#removeDesktopListeners();
    // Remove document/dialog listeners in case we're disconnected while open
    // (e.g. a slideshow re-renders its slides) — otherwise they leak and keep
    // firing against a detached dialog.
    document.body.removeEventListener('click', this.lightDismissMouse);
    document.body.removeEventListener('keydown', this.lightDismissKeyboard);
    document.body.removeEventListener('keyup', this.lightDismissKeyboard);
    this.refs.dialog?.removeEventListener('pointerleave', this.#handlePointerLeave);
    this.#controller?.abort();
    this.#controller = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    if (this.#rafId != null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  /**
   * Open the card on load for `always` mode, once layout is ready.
   * @returns {void}
   */
  #openAlways() {
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      if (!this.isConnected) return;
      this.showDialog();
    });
  }

  /**
   * Recalculate placement while the card is open (used on container resize).
   * @returns {void}
   */
  #repositionIfOpen = () => {
    if (this.#rafId != null) cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      if (this.refs.dialog.open) this.#calculateDialogPlacement();
    });
  };

  /**
   * Toggle the dialog open or closed.
   * @returns {void}
   */
  #toggle() {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  }

  /**
   * Set up desktop event listeners (hover)
   * @returns {void}
   */
  #setupDesktopListeners() {
    const { trigger, dialog } = this.refs;

    /** @type {() => void} */
    const pointerenterHandler = () => {
      if (dialog.open) return;

      this.#timer = setTimeout(() => {
        this.showDialog();
      }, 120);
      // Add pointerleave listener when entering trigger
      trigger.addEventListener('pointerleave', this.#handlePointerLeave);
    };

    this.#pointerenterHandler = pointerenterHandler;
    trigger.addEventListener('pointerenter', pointerenterHandler);
  }

  /**
   * Remove desktop event listeners from trigger
   * @returns {void}
   */
  #removeDesktopListeners() {
    const { trigger } = this.refs;

    if (this.#pointerenterHandler) {
      trigger.removeEventListener('pointerenter', this.#pointerenterHandler);
      trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
      this.#pointerenterHandler = null;
    }

    // Clear any pending timer
    if (this.#timer) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }
  }

  /**
   * Handle breakpoint changes
   * @returns {void}
   */
  #handleBreakpointChange = () => {
    // Remove existing listeners
    this.#removeDesktopListeners();

    // Set up desktop hover listeners only (mobile uses on:click in template)
    if (!isMobileBreakpoint()) {
      this.#setupDesktopListeners();
    }
  };

  /**
   * Calculate the placement of the dialog.
   * @returns {Promise<void> | undefined}
   */
  #calculateDialogPlacement() {
    const { trigger, dialog } = this.refs;

    const hotspotsContainer = this.parentElement;

    if (!hotspotsContainer) {
      return;
    }

    // Spacing constants
    const BUTTON_GAP = 10; // Gap between button and dialog
    const CONTAINER_GAP = 10; // Gap from container edges
    const TOTAL_GAP = BUTTON_GAP + CONTAINER_GAP;

    // Get container bounds
    const containerRect = hotspotsContainer?.getBoundingClientRect();

    // Get button dimensions
    const triggerRect = trigger.getBoundingClientRect();

    // To get dialog dimensions, we need to temporarily show it invisibly
    // Show dialog invisibly to measure it
    dialog.style.visibility = 'hidden';
    dialog.style.display = 'block';
    dialog.style.transform = 'none';
    dialog.removeAttribute('data-placement');

    const { width: dialogWidth, height: dialogHeight } = dialog.getBoundingClientRect();

    // Reset dialog state
    dialog.style.removeProperty('display');
    dialog.style.removeProperty('visibility');
    dialog.style.removeProperty('transform');
    // Calculate button position relative to container
    const buttonLeft = triggerRect.left - containerRect.left;
    const buttonRight = triggerRect.right - containerRect.left;
    const buttonTop = triggerRect.top - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;

    // Calculate available space
    const spaceRight = containerRect.width - buttonRight - CONTAINER_GAP;
    const spaceLeft = buttonLeft - CONTAINER_GAP;

    // Determine horizontal placement
    let x = 'right';

    if (spaceRight >= dialogWidth + BUTTON_GAP) {
      x = 'right';
    } else if (spaceLeft >= dialogWidth + BUTTON_GAP) {
      x = 'left';
    } else {
      x = 'center';
    }

    // Determine vertical placement
    let y = 'bottom';
    let verticalOffset = 0;

    if (x !== 'center') {
      let dialogStartY = buttonTop; // Default to top-aligned
      let dialogEndY = buttonTop + dialogHeight;

      if (dialogEndY > containerRect.height - CONTAINER_GAP) {
        // If top-aligned overflows bottom
        dialogStartY = buttonBottom - dialogHeight;
        dialogEndY = buttonBottom;
        y = 'top';

        if (dialogStartY < CONTAINER_GAP) {
          // If bottom-aligned overflows top
          verticalOffset = CONTAINER_GAP - dialogStartY;
        } else if (dialogEndY > containerRect.height - CONTAINER_GAP) {
          // If bottom-aligned overflows bottom
          verticalOffset = -(dialogEndY - (containerRect.height - CONTAINER_GAP));
        }
      } else {
        if (dialogStartY < CONTAINER_GAP) {
          // If top-aligned overflows top
          verticalOffset = CONTAINER_GAP - dialogStartY;
          y = 'bottom';
        }
      }
    } else {
      // For center horizontal: position below or above button
      if (containerRect.height - buttonBottom >= dialogHeight + TOTAL_GAP) {
        y = 'bottom';
      } else if (buttonTop >= dialogHeight + TOTAL_GAP) {
        y = 'top';
      } else {
        // If neither fits well, choose based on button position
        y = buttonTop < containerRect.height / 2 ? 'bottom' : 'top';
      }
    }

    // Set placement data attribute
    dialog.dataset.placement = `${x},${y}`;

    // Apply vertical offset if needed to keep dialog in bounds
    if (verticalOffset !== 0) {
      dialog.style.setProperty('--dialog-vertical-offset', `${verticalOffset}px`);
    } else {
      dialog.style.removeProperty('--dialog-vertical-offset');
    }

    // Return a promise that resolves after a few ticks to ensure styles are applied
    return new Promise((resolve) => setTimeout(resolve, 100));
  }

  /**
   * Handle pointer leave.
   * @param {PointerEvent} e - The event.
   * @returns {void}
   */
  #handlePointerLeave = (e) => {
    const { dialog, trigger } = this.refs;

    // Clear open timer if leaving trigger before dialog opens
    if (this.#timer) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }

    if (!dialog.open) return;

    const isLeavingTrigger = e.target === trigger;
    const isLeavingDialog = e.target === dialog;
    const isGoingToDialog =
      e.relatedTarget === dialog ||
      (e.relatedTarget instanceof Element && e.relatedTarget.closest('dialog') === dialog);
    const isGoingToTrigger = e.relatedTarget === trigger;

    if ((isLeavingTrigger && !isGoingToDialog) || (isLeavingDialog && !isGoingToTrigger)) {
      this.closeDialog();
    }
  };

  /**
   * Get the product link for the hotspot product.
   * @returns {HTMLAnchorElement | null} The product link or null.
   */
  getHotspotProductLink() {
    return this.refs.productLink || null;
  }

  /**
   * Handle hotspot click. In `hover` mode a desktop click opens the card and a
   * touch tap toggles it; in `click`/`always` mode the marker toggles the card.
   * @param {MouseEvent} e - The click event
   * @returns {void}
   */
  handleHotspotClick = (e) => {
    if (this.#revealMode === 'hover') {
      if (isMobileBreakpoint() || isTouchDevice()) {
        e.preventDefault();
        e.stopPropagation();
        this.#toggle();
      } else {
        this.showDialog();
      }
      return;
    }

    // click / always: the marker toggles the card
    e.preventDefault();
    e.stopPropagation();
    this.#toggle();
  };

  /**
   * Open and position the dialog. In `always` mode it stays persistent (no
   * dismiss listeners); otherwise light dismiss (and hover close) are wired up.
   * @returns {Promise<void>}
   */
  showDialog = async () => {
    const { dialog, trigger } = this.refs;
    const mode = this.#revealMode;
    await this.#calculateDialogPlacement();
    dialog.dataset.showing = 'true';
    trigger.setAttribute('aria-expanded', 'true');

    if (mode === 'always') {
      // Persistent card: open via the `open` attribute rather than show(), so the
      // dialog focusing steps don't steal focus and scroll an ancestor carousel
      // to the last opened card. No light dismiss, no hover close.
      dialog.open = true;
      return;
    }

    dialog.show();

    document.body.addEventListener('click', this.lightDismissMouse);
    document.body.addEventListener('keydown', this.lightDismissKeyboard);
    document.body.addEventListener('keyup', this.lightDismissKeyboard);

    // Hover close only applies in hover mode
    if (mode === 'hover') {
      dialog.addEventListener('pointerleave', this.#handlePointerLeave);
    }
  };

  /**
   * Close the dialog.
   * @returns {Promise<void>}
   */
  closeDialog = async () => {
    const { dialog, trigger } = this.refs;
    // Return focus to the trigger only if focus is currently inside the dialog
    // (i.e. keyboard use) — avoids stealing focus on hover-out close.
    const shouldRestoreFocus = dialog.contains(document.activeElement);
    dialog.dataset.closing = 'true';
    dialog.close();
    trigger.setAttribute('aria-expanded', 'false');
    if (shouldRestoreFocus) trigger.focus();
    document.body.removeEventListener('click', this.lightDismissMouse);
    document.body.removeEventListener('keydown', this.lightDismissKeyboard);
    document.body.removeEventListener('keyup', this.lightDismissKeyboard);
    // Remove pointerleave listeners when closing
    dialog.removeEventListener('pointerleave', this.#handlePointerLeave);
    trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
    // we need to use a data-attribute to keep transition-behavior working only when open
    const animations = dialog.getAnimations({ subtree: true });
    await Promise.allSettled(animations.map((a) => a.finished));
    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.closing;
      delete dialog.dataset.placement;
    }
  };

  /**
   * Light dismiss the dialog.
   * @param {MouseEvent} event - The event.
   * @returns {void}
   */
  lightDismissMouse = (event) => {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Light dismiss the dialog.
   * @param {KeyboardEvent} event - The event.
   * @returns {void}
   */
  lightDismissKeyboard = (event) => {
    const { dialog } = this.refs;
    if (
      (event.type === 'keydown' && event.key === 'Escape') ||
      // Close when focus tabs out of the dialog. Scoped to Tab so unrelated
      // keystrokes elsewhere on the page don't dismiss a hover-revealed card.
      (event.type === 'keyup' &&
        event.key === 'Tab' &&
        !dialog.matches(':is(:focus, :focus-visible, :focus-within)'))
    ) {
      this.closeDialog();
    }
  };
}

// Register custom element
if (!customElements.get('product-hotspot-component')) {
  customElements.define('product-hotspot-component', ProductHotspotComponent);
}
